import SessionDetailsItem from "@app.automotus.io/components/SessionDetailsItem";
import TextField from "@mui/material/TextField";
import React from "react";
import * as Yup from "yup";
import Box from "@mui/material/Box";
import { Field, useField, useFormikContext } from "formik";
import Autocomplete from "@mui/material/Autocomplete";
import { LicensePlateState } from "@app.automotus.io/components/common";

export const VehicleSessionDetailsItem: React.FC<VehicleSessionDetailsItemProps> = ({
  loading,
  licensePlateNumber,
  licensePlateRegion,
  skeletonWidth,
  onSubmitEdit,
}) => {
  const initialValues: VehicleSessionDetailsItemFormValues = {
    licensePlateNumber: licensePlateNumber || "",
    licensePlateRegion: licensePlateRegion || "",
  };

  const validationSchema = Yup.object({
    licensePlateNumber: Yup.string().required("License plate number is required"),
    licensePlateRegion: Yup.string()
      .matches(/^us-[a-z]{2}$/, "Must be a valid plate region")
      .required("License plate region is required"),
  });

  const regionLabel = licensePlateRegion && licensePlateRegion.split("-")[1].toUpperCase();
  const content = licensePlateNumber && regionLabel && `${regionLabel} ${licensePlateNumber}`;

  return (
    <SessionDetailsItem
      loading={loading}
      title={"Vehicle"}
      content={content}
      skeletonWidth={skeletonWidth}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmitEdit={onSubmitEdit}
    >
      {({ isSubmitting, errors, touched, setFieldValue }) => (
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", justifyContent: "space-between" }}>
          <LicensePlateRegionAutocomplete />
          <Field
            as={TextField}
            name="licensePlateNumber"
            autoFocus
            fullWidth
            error={touched.licensePlateNumber && Boolean(errors.licensePlateNumber)}
            helperText={(touched.licensePlateNumber && errors.licensePlateNumber) || " "}
            inputProps={{ maxLength: 9 }}
            disabled={isSubmitting}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setFieldValue("licensePlateNumber", e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))
            }
          />
        </Box>
      )}
    </SessionDetailsItem>
  );
};

const LicensePlateRegionAutocomplete = () => {
  const { isSubmitting } = useFormikContext();
  const [field, meta, helpers] = useField<string>("licensePlateRegion");
  const showError = meta.touched && Boolean(meta.error);

  return (
    <Autocomplete<string, false, false, false>
      fullWidth
      value={meta.value}
      onChange={(e, v) => v !== null && helpers.setValue(v)}
      onBlur={field.onBlur}
      disabled={isSubmitting}
      options={LicensePlateState.All.map((lps) => `${lps.countryCode}-${lps.abbreviation}`.toLowerCase())}
      getOptionLabel={(opt) =>
        LicensePlateState.All.find(
          ({ abbreviation, countryCode }) => `${countryCode}-${abbreviation}`.toLowerCase() === opt,
        )?.abbreviation || ""
      }
      renderInput={(params) => (
        <TextField {...params} name="licensePlateRegion" error={showError} helperText={showError ? meta.error : " "} />
      )}
    />
  );
};

export interface VehicleSessionDetailsItemFormValues {
  licensePlateNumber: string;
  licensePlateRegion: string;
}

export interface VehicleSessionDetailsItemProps {
  loading?: boolean;
  licensePlateNumber?: string;
  licensePlateRegion?: string;
  skeletonWidth?: number;
  onSubmitEdit?: (values: VehicleSessionDetailsItemFormValues) => Promise<void>;
}

export default VehicleSessionDetailsItem;
